import { InvoiceDataResponse } from "../lib/types";
import {
  createForexCustomer,
  createForexOrder,
  type CreateForexOrderPayload,
  listForexCountries,
  liveRateForWizard,
  searchForexCustomers,
} from "service/moneeflo/legacy-forex-create-api";
import { addForexLines, listCustomerAddresses } from "service/moneeflo/legacy-forex-desk-api";
import { notAvailable } from "service/moneeflo/not-available";

/*
 * The forex create-order wizard. Customer search, adding a customer and the
 * country list are answered by the new backend; everything that has no
 * staff-side endpoint there yet says "not available yet" and stops. See
 * service/moneeflo/legacy-forex-create-api.ts.
 */

export interface CustomerResponse {
  phone_number: string;
  full_name: string;
  email: string;
  pan_no: string;
  id: string;
  kyc_status?: string;
}
export interface AddCurrencyParams {
  currency_have: string;
  currency_want: string;
  city_country: string;
  product_id: number;
  fx_amount: number;
  inr_amount: number;
  rate: number;
  customers_id: number;
}

// Update: AddCurrencyPayload expects product as an array
export interface AddCurrencyPayload {
  order_details_id: number;
  channels_id: number;
  branch: number;
  product: AddCurrencyParams[];
}


export interface CalculationPayload {
  amount: number;
  type: string;
}

export type ApplyCouponnOrderRequestType = {
  generic_order_details_id: number;
  discount_coupon_id: (string | number)[];
};


export interface CalculationResponse {
  amount: number;
  CGST: number;
  SGST: number;
  roundOff: number;
  service_charge: number;
  gst: {
    slab1: number;
    slab2: number;
    slab3: number;
    total_taxable_amount: number;
    gst: number;
  };
  total_payable: {
    grand_total: number;
  };
}

export interface CalculationPayload {
  amount: number;
  type: string;
}

export interface CalculationResponse {
  amount: number;
  CGST: number;
  SGST: number;
  IGST?: number;
  roundOff: number;
  service_charge: number;
  gst: {
    slab1: number;
    slab2: number;
    slab3: number;
    total_taxable_amount: number;
    gst: number;
  };
  total_payable: {
    grand_total: number;
  };
}



export type OrderPayload = {
  invoice_uploaded?: boolean;
  invoice_abt?: number; // amount before tax
  invoice_grand_total?: number;
  invoice_gst?: number;
  invoice_sgst?: number;
  invoice_cgst?: number;
  invoice_igst?: number;
  invoice_service_charge?: number;
  invoice?: string; // base64 encoded string
  purpose: string;
  end_date: string; // ISO format e.g., "2025-05-23"
  start_date: string;
  type_of_order: "buy" | "sell";
  opportunity_id: number;
  discount_coupon_id: [
    any
  ];
  products: {
    customers_id: number | null;
    forex_type: "Sale" | "Purchase";
    currency_have: string;
    quantity: number;
    card_type: "multi currency" | "single currency";
    product_id: number;
    currency_want: string;
    rate: number;
    local_id: string; // new field added
    name?: string;
    pan_no?: string;
    phone_number?: string;
    email?: string;
  }[];
  delivery_details: Partial<{
    local_id: string[]; // links to one or more products
    customers_id: number;
    address_id: string; // UUID string
    delivery_mode: string;
    delivery_id: string;
    delivery_channel: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  }[]>;
};


export type PassportUpdateOrder = {
  passport_front: string;
  passport_back: string;
  customers_id: number;
  pan_name: string;
}

// The rate itself is the one the dashboard uses; this only reports a failure.
export const getLiveRates = liveRateForWizard

export const getCustomers = async (number: string): Promise<CustomerResponse[]> => {
  return await searchForexCustomers(number)
}

// Add currency lines to the order on screen.
export const addCurrency = async (data: { lines: { currency: string; fxAmount: number; productId?: number }[] }): Promise<any> => {
  return await addForexLines(data)
};

export const adddNewContact = async ({ name, pan_no, phone_number, email }: { name: string, pan_no: string, phone_number: string, email: string }): Promise<any> => {
  return await createForexCustomer({ name, pan_no, phone_number, email })
}

export const createGenericOrder = async (data: CreateForexOrderPayload): Promise<any> => {
  // The order's reference goes back where the wizard reads it to open the order.
  return { data: await createForexOrder(data) }
}

// The customer's saved addresses, in the shape the delivery form reads.
export const getAddressById = async (data: { customers_id: string }): Promise<any> => {
  const addresses = await listCustomerAddresses(data.customers_id)
  return {
    address_id: addresses.map((a) => ({
      id: a.id,
      house_number: a.line1,
      street_number: a.line2 ?? "",
      City: a.city,
      State: a.state ?? "",
      pincode: a.postalCode,
      landmark: a.landmark ?? "",
      country_name: a.country,
    })),
  }
}

export const getCountryList = async (): Promise<{
  name: {
    common: string;
    official: string;
  }
}[]> => {
  return await listForexCountries()
}

export const getInvoiceData = async (_file: File): Promise<InvoiceDataResponse> => {
  return notAvailable("Reading an invoice automatically")
}

export const generateLink = async (_data: { file: File, type: string }): Promise<any> => {
  return notAvailable("Uploading a KYC document for automatic reading")
}

export const panVerification = async (_data: { link: string }): Promise<any> => {
  return notAvailable("Reading a PAN card automatically")
}

export const panUpdate = async (_data: { customers_id: number, pan_no: string, pan_image: string, pan_name: string }): Promise<any> => {
  return notAvailable("Verifying a PAN card automatically")
}

export const passportUpdateOrder = async (_data: PassportUpdateOrder): Promise<{
  result1: string;
  addressID: string;
  kyc: string;
}> => {
  return notAvailable("Verifying a passport automatically")
}
